/**
 * Secure GetTranscribe Service
 * Uses server-side API to keep API key secure
 */

class GetTranscribeService {
  /**
   * Transcribe a video from URL (Instagram, TikTok, YouTube)
   * @param {string} url - The video URL to transcribe
   * @param {string} language - ISO-639-1 language code (optional)
   * @param {string} prompt - Context prompt for better transcription (optional)
   * @returns {Promise<Object>} Transcription result
   */
  async transcribeVideo(url, language = 'en', prompt = null) {
    try {
      console.log('🎬 Starting secure server-side GetTranscribe transcription for:', url);
      console.log('🌐 Calling /api/gettranscribe endpoint');
      
      const response = await fetch('/api/gettranscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          url,
          language,
          prompt
        })
      });

      console.log('📡 GetTranscribe response status:', response.status);
      console.log('📡 GetTranscribe response ok:', response.ok);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ GetTranscribe API error:', response.status, errorText);
        try {
          const errorData = errorText ? JSON.parse(errorText) : { error: 'Unknown error' };
          // Don't throw - return error object so we can detect no-audio
          return {
            success: false,
            data: null,
            error: errorData.error || JSON.stringify(errorData) || `HTTP ${response.status}: ${errorText}`
          };
        } catch (parseError) {
          // If JSON parsing fails, still return error object
          return {
            success: false,
            data: null,
            error: errorText || `HTTP ${response.status}: Failed to parse error`
          };
        }
      }

        const data = await response.json();
        console.log('✅ GetTranscribe response received:', data);
        
        // Check if there's an error from the serverless function
        if (data.error) {
          console.error('❌ Transcription error:', data.error);
          return {
            success: false,
            data: null,
            error: data.error
          };
        }
        
        return {
          success: true,
          data: data.data,
          error: null
        };

      } catch (error) {
        console.error('❌ GetTranscribe Error:', error);
        console.error('❌ Error stack:', error.stack);
        return {
          success: false,
          data: null,
          error: error.message
        };
      }
    }

  /**
   * Check if content needs transcription
   * @param {string} contentType - Type of content
   * @param {string} channel - Channel/platform
   * @returns {boolean} Whether transcription is needed
   */
  needsTranscription(contentType, channel) {
    const videoTypes = ['reel', 'video', 'story', 'live'];
    const videoChannels = ['Instagram', 'TikTok', 'YouTube', 'Facebook'];
    
    return videoTypes.includes(contentType?.toLowerCase()) || 
           videoChannels.includes(channel);
  }
}

export default new GetTranscribeService();
